/**
 * Slice A/B: working set editor + native compute + pick option + CSV export.
 * Deterministic engine only — LLM does not invent schedules.
 */

import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Button } from '@/components/ui/Button';
import {
  SAMPLE_SCHED_WORKING_SET,
  computeRun,
  selectRunCandidate,
  updateRunWorkingSet,
} from '@/services/aiStudioAgentService';
import type { AiStudioRun } from '@/types/aiStudioAgent';

interface RunComputePanelProps {
  run: AiStudioRun;
  onRunUpdated: (run: AiStudioRun) => void;
  onDecisionCaseOpened?: (caseId: string) => void;
}

interface CandidateView {
  optionId: string;
  label: string;
  scores?: {
    onTimeRatio?: number;
    lateDemandCount?: number;
    utilization?: number;
    lateValueAtRisk?: number;
    weightedScore?: number;
  };
  assignments?: Array<{
    taskId: string;
    resourceId: string;
    startMinutes: number;
    endMinutes: number;
  }>;
  notes?: string[];
}

function asCandidates(computeResult: Record<string, unknown> | null): CandidateView[] {
  if (!computeResult) return [];
  const raw = computeResult.candidates;
  if (!Array.isArray(raw)) return [];
  return raw.map((c) => c as CandidateView);
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Prefer API error body; fall back to Error.message. */
function apiErrorMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const data = (e as { response?: { data?: { error?: string } } }).response?.data;
    if (data?.error && typeof data.error === 'string') return data.error;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export const RunComputePanel: React.FC<RunComputePanelProps> = ({
  run,
  onRunUpdated,
  onDecisionCaseOpened,
}) => {
  const { t } = useLanguage();
  const [draftJson, setDraftJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const source =
      run.workingSet && Object.keys(run.workingSet).length > 0
        ? run.workingSet
        : SAMPLE_SCHED_WORKING_SET;
    setDraftJson(JSON.stringify(source, null, 2));
    setJsonError(null);
  }, [run.id, run.workingSet]);

  const candidates = asCandidates(run.computeResult);
  const selectedId =
    typeof run.currentThesis.selectedOptionId === 'string'
      ? run.currentThesis.selectedOptionId
      : null;

  const handleLoadSample = () => {
    setDraftJson(JSON.stringify(SAMPLE_SCHED_WORKING_SET, null, 2));
    setJsonError(null);
  };

  const handleSaveWorkingSet = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const parsed = JSON.parse(draftJson) as Record<string, unknown>;
      const updated = await updateRunWorkingSet(run.id, parsed);
      onRunUpdated(updated);
    } catch (e) {
      if (e instanceof SyntaxError) {
        setJsonError(e.message);
      } else {
        setActionError(apiErrorMessage(e, 'Could not save working set'));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCompute = async () => {
    setBusy(true);
    setActionError(null);
    setJsonError(null);
    try {
      // Persist draft first so compute uses what the user sees.
      const parsed = JSON.parse(draftJson) as Record<string, unknown>;
      await updateRunWorkingSet(run.id, parsed);
      const result = await computeRun(run.id, 'dispatch');
      onRunUpdated(result.run);
      if (result.decisionCaseId && onDecisionCaseOpened) {
        onDecisionCaseOpened(result.decisionCaseId);
      }
    } catch (e) {
      if (e instanceof SyntaxError) {
        setJsonError(e.message);
      } else {
        setActionError(apiErrorMessage(e, t('aiStudio.workspace.computeFailed')));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSelect = async (optionId: string) => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await selectRunCandidate(run.id, optionId);
      onRunUpdated(updated);
    } catch (e) {
      setActionError(apiErrorMessage(e, t('aiStudio.workspace.selectFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handleExport = (candidate: CandidateView) => {
    const rows: string[][] = [
      ['taskId', 'resourceId', 'startMinutes', 'endMinutes'],
      ...(candidate.assignments ?? []).map((a) => [
        a.taskId,
        a.resourceId,
        String(a.startMinutes),
        String(a.endMinutes),
      ]),
    ];
    downloadCsv(`schedule-${candidate.optionId}.csv`, rows);
  };

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.workspace.computeTitle')}
      </h2>
      <p className="text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.workspace.computeHint')}
      </p>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={handleLoadSample}>
          {t('aiStudio.workspace.loadSample')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          loading={busy}
          onClick={() => void handleSaveWorkingSet()}
        >
          {t('aiStudio.workspace.saveWorkingSet')}
        </Button>
        <Button
          type="button"
          variant="success"
          size="sm"
          disabled={busy}
          loading={busy}
          onClick={() => void handleCompute()}
        >
          {t('aiStudio.workspace.generateOptions')}
        </Button>
      </div>

      <textarea
        className="min-h-[160px] w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-2 font-mono text-base text-[var(--color-surface-text)]"
        value={draftJson}
        disabled={busy}
        onChange={(e) => setDraftJson(e.target.value)}
        aria-label={t('aiStudio.workspace.workingSetLabel')}
      />
      {jsonError && <p className="text-base text-[var(--color-error)]">{jsonError}</p>}
      {actionError && <p className="text-base text-[var(--color-error)]">{actionError}</p>}

      {candidates.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-base font-medium text-[var(--color-surface-text)]">
            {t('aiStudio.workspace.candidatesTitle')}
          </h3>
          {candidates.map((c, index) => {
            const letter = String.fromCharCode(65 + index);
            const isSelected = selectedId === c.optionId;
            return (
              <div
                key={c.optionId}
                className={`rounded border p-3 ${
                  isSelected
                    ? 'border-[var(--color-success)] bg-[var(--color-success)]/10'
                    : 'border-[var(--color-border)] bg-[var(--color-surface-bg)]'
                }`}
              >
                <p className="font-medium text-[var(--color-surface-text)]">
                  {t('aiStudio.workspace.optionLetter', { letter })}: {c.label}
                </p>
                <p className="mt-1 text-base text-[var(--color-surface-text-secondary)]">
                  {t('aiStudio.workspace.scoreLine', {
                    onTime: String(Math.round((c.scores?.onTimeRatio ?? 0) * 100)),
                    util: String(Math.round((c.scores?.utilization ?? 0) * 100)),
                    late: String(c.scores?.lateDemandCount ?? 0),
                    risk: String(c.scores?.lateValueAtRisk ?? 0),
                    weighted: String(c.scores?.weightedScore ?? 0),
                  })}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={isSelected ? 'success' : 'primary'}
                    size="sm"
                    disabled={busy}
                    onClick={() => void handleSelect(c.optionId)}
                  >
                    {isSelected
                      ? t('aiStudio.workspace.selected')
                      : t('aiStudio.workspace.selectOption')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => handleExport(c)}
                  >
                    {t('aiStudio.workspace.exportCsv')}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
